// TODO: Consider removing it, we shouldn't save large strings anyway to local storage and this bloats the vendors bundle
import pako from 'pako'

import { getUrlParam } from 'utils/UrlUtils'
import { logger } from './logger'

/* global localStorage */

const STRING_LENGTH_COMPRESSION_THRESHOLD = 50000 // Above this size, compress the string before saving to localStorage
const COMPRESSED_STRING_MAGIC = '#compressed#'

// Firefox names a full localStorage NS_ERROR_DOM_QUOTA_REACHED, and older
// WebKit sends only the legacy QUOTA_EXCEEDED_ERR code, 22.
const QUOTA_EXCEEDED_ERROR_NAMES = [
  'QuotaExceededError',
  'NS_ERROR_DOM_QUOTA_REACHED',
]
const QUOTA_EXCEEDED_ERROR_CODE = 22

export function isStorageQuotaExceededError(err: unknown): boolean {
  return (
    (err instanceof Error && QUOTA_EXCEEDED_ERROR_NAMES.includes(err.name)) ||
    (err instanceof DOMException && err.code === QUOTA_EXCEEDED_ERROR_CODE)
  )
}

function serializeStorageValue<T>(key: string, value: T | null): string {
  // Always initialize as a string; JSON.stringify(null) returns "null"
  const valueString: string = JSON.stringify(value)

  // Compress the string if needed (don't compress experiments or tiles)
  if (
    value === null ||
    valueString.length <= STRING_LENGTH_COMPRESSION_THRESHOLD ||
    ['tiles', 'experiments'].includes(key)
  ) {
    return valueString
  }

  try {
    return (
      COMPRESSED_STRING_MAGIC +
      pako.deflate(valueString, {
        to: 'string',
      } as unknown as pako.DeflateFunctionOptions)
    )
  } catch (error) {
    logger.error(
      'Error while compressing value, saving without compression',
      error,
      { key }
    )
    return valueString
  }
}

class StorageManager {
  static set<T>(key: string, value: T | null): void {
    if (value === undefined) return

    try {
      localStorage.setItem(key, serializeStorageValue(key, value))
    } catch (e) {
      logger.warning('Write to local storage failed', {
        error: e,
        key,
      })
    }
  }

  // For callers that must know the write landed: a full quota rejects it.
  static setOrThrow<T>(key: string, value: T): void {
    localStorage.setItem(key, serializeStorageValue(key, value))
  }

  static get<T>(key: string): T | null {
    let value = localStorage.getItem(key)
    if (value === undefined) return null

    // Decompress the string if needed
    if (value && value.startsWith(COMPRESSED_STRING_MAGIC)) {
      value = pako.inflate(
        value.slice(COMPRESSED_STRING_MAGIC.length) as unknown as pako.Data,
        { to: 'string' }
      )
    }

    try {
      return value ? (JSON.parse(value) as T) : null
    } catch (e) {
      logger.warning('Failed to parse stored value', {
        error: e,
        key,
        value,
      })
      return null
    }
  }

  static getWithDefaultValue<T>(key: string, defaultValue: T): T {
    let value = this.get<T>(key)
    if (value == null) {
      this.set<T>(key, defaultValue)
      value = defaultValue
    }
    return value
  }

  static remove(key: string): boolean {
    localStorage.removeItem(key)
    return true
  }

  static hasKey(key: string): boolean {
    return Object.prototype.hasOwnProperty.call(localStorage, key)
  }

  static setIfEmpty<T>(key: string, defaultValue: T = {} as T): void {
    if (this.get<T>(key) == null) {
      this.set<T>(key, defaultValue)
    }
  }

  static setFromUrlParam<T = string>(
    param: string,
    key: string,
    getValueFunction?: (paramValue: string | null) => T
  ): void {
    const paramValue = getUrlParam(param)
    if (paramValue !== undefined) {
      StorageManager.set<T>(
        key,
        getValueFunction
          ? getValueFunction(paramValue)
          : (paramValue as unknown as T)
      )
    }
  }

  static getKeysByRegex(regex: RegExp): string[] {
    return Object.keys(localStorage).filter((key) => regex.test(key))
  }
}

export default StorageManager
